import { airHandlerTypeCodes } from 'data/constants';
import { AirHandlerTypeCodeType, SystemOptionType } from 'data/types';
import { abbreviateNumber, formatGroupedCounts } from 'utils/helpers';
import { isPtacSelectedSystem } from 'utils/ptac-selected-system';

const DUCTED_AIR_HANDLER_CODES: ReadonlySet<AirHandlerTypeCodeType> = new Set([
  'ducted',
  'evap_coil'
]);

// Product truth, deliberately NOT zone.type: the API load path now derives
// zone type from condenser/air-handler codes, but a hybrid ducted zone with
// no placed air handler still reloads as 'ductless' (type isn't persisted),
// while the selected product's type label always survives.
export function systemOptionIncludesDuctedAirHandler(
  system: SystemOptionType
): boolean {
  return system.configuration.condensers.some((condenser) =>
    condenser.zones.some((zone) => {
      const code = (
        airHandlerTypeCodes as Record<
          string,
          AirHandlerTypeCodeType | undefined
        >
      )[zone.selectedProduct?.type ?? ''];
      return code !== undefined && DUCTED_AIR_HANDLER_CODES.has(code);
    })
  );
}

export type SystemConfigurationSummaryLines = {
  condenserLine: string;
  zoneLine: string;
};

// A Hotel Style PTAC system is N through-the-wall units, one per room: the
// units ARE the system, so the summary reads units and rooms rather than
// condensers and zones.
function ptacSummaryLines(
  system: SystemOptionType
): SystemConfigurationSummaryLines {
  const { condensers } = system.configuration;
  const unitSizes = condensers
    .map((cond) => cond.selectedProduct?.coolingBTU ?? 0)
    .filter((btu) => btu > 0)
    .map((btu) => abbreviateNumber(btu));
  const unitSummary = formatGroupedCounts(unitSizes);
  const roomCount = condensers.reduce(
    (sum, cond) =>
      sum +
      cond.zones.reduce((zoneSum, zone) => zoneSum + zone.zoneItems.length, 0),
    0
  );
  return {
    condenserLine: `${condensers.length} PTAC Unit${
      condensers.length === 1 ? '' : 's'
    }${unitSummary ? ` (${unitSummary})` : ''}`,
    zoneLine: `${roomCount} Room${roomCount === 1 ? '' : 's'}`
  };
}

export function getSystemConfigurationSummaryLines(
  system: SystemOptionType
): SystemConfigurationSummaryLines {
  if (isPtacSelectedSystem(system)) return ptacSummaryLines(system);
  const { condensers } = system.configuration;

  const condenserCount = condensers.length;
  const condenserSizes = condensers
    .map((cond) => cond.selectedProduct?.coolingBTU ?? 0)
    .filter((btu) => btu > 0)
    .map((btu) => abbreviateNumber(btu));
  const condenserSummary = formatGroupedCounts(condenserSizes);

  const zoneCount = condensers.reduce(
    (sum, cond) => sum + cond.zones.length,
    0
  );
  const zoneSummaryLabels = condensers
    .flatMap((cond) =>
      cond.zones.map((zone) => {
        const btu = zone.selectedProduct?.coolingBTU ?? 0;
        const type = zone.selectedProduct?.type ?? '';
        if (btu <= 0) return null;
        return type
          ? `${abbreviateNumber(btu)} ${type}`
          : abbreviateNumber(btu);
      })
    )
    .filter((label): label is string => label !== null);
  const airHandlerSummary = formatGroupedCounts(zoneSummaryLabels);

  const condenserLine = `${condenserCount} Condenser${
    condenserCount === 1 ? '' : 's'
  }${condenserSummary ? ` (${condenserSummary})` : ''}`;
  const zoneLine = `${zoneCount} Zone${zoneCount === 1 ? '' : 's'}${
    airHandlerSummary ? ` (${airHandlerSummary})` : ''
  }`;

  return { condenserLine, zoneLine };
}
